import { useEffect, useRef } from 'react';

/**
 * Plays a one-shot chime when a prototype generation completes for the first
 * time (deduped per prototype id via localStorage). Self-contained: owns the
 * audio element and the "was creating" edge-detection ref.
 */
export function useConversationSound(params: {
  isPrototypeCreating: boolean;
  isNewlyGenerated: boolean | undefined;
  prototypeId: string | number | null | undefined;
}): void {
  const { isPrototypeCreating, isNewlyGenerated, prototypeId } = params;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const prevIsCreatingRef = useRef(isPrototypeCreating);

  useEffect(() => {
    if (typeof Audio === 'undefined') {
      return;
    }
    const audio = new Audio('https://files.ideoz.ai/mixkit-positive-interface-beep-221.wav');
    audio.preload = 'auto';
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const wasCreating = prevIsCreatingRef.current;
    prevIsCreatingRef.current = isPrototypeCreating;
    // Play sound only when prototype creation just finished and is newly generated
    if (wasCreating && !isPrototypeCreating && isNewlyGenerated && prototypeId) {
      const playedKey = `prototype_sound_played_${prototypeId}`;
      if (!localStorage.getItem(playedKey)) {
        const audio = audioRef.current;
        if (audio) {
          localStorage.setItem(playedKey, 'true');
          audio.currentTime = 0;
          const playResult = audio.play();
          if (playResult && typeof playResult.catch === 'function') {
            playResult.catch((error) => {
              console.warn('Prototype completion sound could not play automatically:', error);
            });
          }
        }
      }
    }
    // Deps match the original effect exactly (isNewlyGenerated read but not a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrototypeCreating, prototypeId]);
}
